/**
 * @file Language.jsx
 * @description Language component for displaying and managing language.
 * @component Language
 * @exports Language - Main component for displaying and managing language.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Alert, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { DASHBOARD_ERRORS } from "../../../common/constants/dashboardModule/dashboardConstants.js";
import { LANGUAGE_TABS, LANGUAGE_STRINGS, formatDateTime } from "../../../common/constants/dashboardModule/languageConstants.js";
import {
    fetchLanguageData,
    fetchLanguageConversations,
} from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import LanguageDailyStackedAreaChart from "./LanguageChart.jsx";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PURPLE = "#7F77DD";

// English/Spanish color map for chip badges in the drill-down dialog
const LANGUAGE_COLORS = {
    English: BLUE,
    Spanish: PURPLE,
};

// ── Column config ──────────────────────────────────────────────────
const COLS = [
    { key: "ConversationDate",   label: "Conv. Date",  width: 120, align: "left"  },
    { key: "English",            label: "English",     width: 90,  align: "right" },
    { key: "EnglishPercentage",  label: "English %",   width: 100, align: "right" },
    { key: "Spanish",            label: "Spanish",     width: 90,  align: "right" },
    { key: "SpanishPercentage",  label: "Spanish %",   width: 100, align: "right" },
    { key: "Total",              label: "Total",       width: 90,  align: "right" },
];

// 40px column at the start for the drill icon
const TABLE_COLS    = [{ key: "_drill", label: "", width: 40, align: "left" }, ...COLS];
const GRID_COLS     = TABLE_COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH   = TABLE_COLS.reduce((s, c) => s + c.width, 0) + 64;
const ROWS_PER_PAGE = 25;

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
    if (!val) return null;
    const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

const formatDate = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return val || "—";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

function PctPill({ value, color, onClick, clickable }) {
    if (!value && value !== 0) return <Box sx={{ color: "text.disabled" }}>—</Box>;
    return (
        <Box
            component="span"
            onClick={clickable ? onClick : undefined}
            sx={{
                display: "inline-block",
                px: "7px", py: "1px", borderRadius: "20px",
                fontSize: 11, fontWeight: 500,
                bgcolor: `${color}18`,
                color,
                border: `0.5px solid ${color}44`,
                cursor: clickable ? "pointer" : "default",
                "&:hover": clickable ? { bgcolor: `${color}28` } : {},
            }}
        >
            {value}
        </Box>
    );
}

// ── Component ──────────────────────────────────────────────────────
const Language = () => {
    const today          = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate,    setStartDate]    = useState(firstDayOfMonth);
    const [endDate,      setEndDate]      = useState(today);
    const [languageType, setLanguageType] = useState(LANGUAGE_TABS.TEXTS);
    const [languageData, setLanguageData] = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [error,        setError]        = useState("");
    const [page,         setPage]         = useState(0);
    const [sortKey,      setSortKey]      = useState(null);
    const [sortDir,      setSortDir]      = useState("asc");
    const [drill,        setDrill]        = useState(null);

    useEffect(() => {
        load(firstDayOfMonth, today, LANGUAGE_TABS.TEXTS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (start, end, type) => {
        try {
            setLoading(true); setError(""); setPage(0);
            setLanguageData((await fetchLanguageData({
                startDate:    formatDateTime(start),
                endDate:      formatDateTime(end),
                languageType: type,
            })) || []);
        } catch (e) {
            console.error(DASHBOARD_ERRORS.FETCH_LANGUAGE_ERROR, e);
            setError("Failed to load language data.");
        } finally {
            setLoading(false);
        }
    };

    const handleSortCol = (key) => {
        if (key === "_drill") return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortKey) return languageData;
        return [...languageData].sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const an = Number(av), bn = Number(bv);
            const cmp = !isNaN(an) && !isNaN(bn)
                ? an - bn
                : String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [languageData, sortKey, sortDir]);

    const pagedData  = useMemo(() =>
        sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sortedData, page]);

    const totalPages = Math.ceil(languageData.length / ROWS_PER_PAGE);

    const kpis = useMemo(() => {
        const sum    = (k) => languageData.reduce((a, r) => a + (Number(r[k]) || 0), 0);
        const avgPct = (k) => languageData.length
            ? languageData.reduce((a, r) => a + (parseFloat(r[k]) || 0), 0) / languageData.length
            : 0;
        return {
            total:      sum("Total"),
            english:    sum("English"),
            spanish:    sum("Spanish"),
            engPct:     avgPct("EnglishPercentage").toFixed(1),
            spaPct:     avgPct("SpanishPercentage").toFixed(1),
        };
    }, [languageData]);

    // ── Drill-down ────────────────────────────────────────────────
    // language only       → all conversations w/ that language in picker range
    // date only           → all conversations on that date (any language)
    // language + date     → that language on that single date
    const openDrill = useCallback(({ language = null, date = null }) => {
        const langColor = language ? LANGUAGE_COLORS[language] : null;

        const title =
            language && date ? "Conversations by language & date" :
            language         ? `Conversations: ${language}` :
                               `Conversations on ${formatDate(date)}`;

        const dStart = date ? new Date(date) : startDate;
        const dEnd   = date ? new Date(date) : endDate;

        setDrill({
            title,
            metric: language ? { label: "Language", value: language, color: langColor } : null,
            context: [
                date && { label: "Date", value: formatDate(date) },
                !date && startDate && { label: "From", value: formatDate(startDate) },
                !date && endDate   && { label: "To",   value: formatDate(endDate) },
            ].filter(Boolean),
            channel: languageType,
            emotionColors: LANGUAGE_COLORS,
            fetchData: () => fetchLanguageConversations({
                language,
                startDate:   dStart ? dStart.toISOString() : null,
                endDate:     dEnd   ? dEnd.toISOString()   : null,
                channeltype: languageType,
            }),
        });
    }, [startDate, endDate, languageType]);

    // ── CSV export ────────────────────────────────────────────────
    const exportDisabled = loading || sortedData.length === 0;

    const exportCsv = () => {
        if (!sortedData.length) return;
        const headers = COLS.map(c => csvEscape(c.label));
        const rows = sortedData.map(row =>
            COLS.map(col => {
                if (col.key === "ConversationDate") return csvEscape(formatDate(row.ConversationDate));
                return csvEscape(row[col.key]);
            }).join(",")
        );
        const csv  = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);
        const now = new Date();
        const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
        const channel = String(languageType).toLowerCase();
        const a = document.createElement("a");
        a.href = url;
        a.download = `language_${channel}_${ts}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* Filters */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent label={STRINGS.START_DATE} date={startDate} onDateChange={setStartDate} marginRight={0} />
                <DatePickerComponent label={STRINGS.END_DATE}   date={endDate}
                    onDateChange={d => { if (d >= startDate) setEndDate(d); }} marginRight={0} />
                <Box onClick={() => load(startDate, endDate, languageType)} sx={{
                    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none",
                    background: BLUE, color: "#fff", fontWeight: 500,
                    alignSelf: "flex-end", mb: "1px",
                    "&:hover": { background: "#2a6db5" },
                }}>Search</Box>
                <Box
                    onClick={exportDisabled ? undefined : exportCsv}
                    title={exportDisabled ? "No data to export" : "Export current view to CSV"}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        userSelect: "none",
                        border: "0.5px solid",
                        borderColor: exportDisabled ? "divider" : BLUE,
                        background: "transparent",
                        color: exportDisabled ? "text.disabled" : BLUE,
                        fontWeight: 500,
                        cursor: exportDisabled ? "default" : "pointer",
                        opacity: exportDisabled ? 0.5 : 1,
                        alignSelf: "flex-end", mb: "1px",
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        transition: "all .15s",
                        "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}
                >
                    <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
                    Export CSV
                </Box>
                <Box sx={{
                    display: "flex", ml: "auto",
                    border: "0.5px solid", borderColor: "divider",
                    borderRadius: "20px", overflow: "hidden",
                    alignSelf: "flex-end",
                }}>
                    {[LANGUAGE_TABS.TEXTS, LANGUAGE_TABS.EMAILS].map(ch => (
                        <Box key={ch} onClick={() => { setLanguageType(ch); load(startDate, endDate, ch); }} sx={{
                            px: "14px", py: "5px", fontSize: 13,
                            fontWeight:  languageType === ch ? 500 : 400,
                            cursor: "pointer", userSelect: "none",
                            background:  languageType === ch ? BLUE : "transparent",
                            color:       languageType === ch ? "#fff" : "text.secondary",
                            transition:  "all .15s",
                        }}>{ch}</Box>
                    ))}
                </Box>
            </Box>

            {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && languageData.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {LANGUAGE_STRINGS.NO_RESULTS}
                </Typography>
            )}

            {!loading && languageData.length > 0 && (
                <>
                    {/* KPIs — English/Spanish cards drill the language across the range */}
                    <SectionTitle>Summary</SectionTitle>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {[
                            { label: "Total Records", value: kpis.total.toLocaleString(),   color: BLUE,   onClick: undefined                                       },
                            { label: "English",       value: kpis.english.toLocaleString(), color: BLUE,   onClick: () => openDrill({ language: "English" })       },
                            { label: "English %",     value: `${kpis.engPct}%`,             color: BLUE,   onClick: () => openDrill({ language: "English" })       },
                            { label: "Spanish",       value: kpis.spanish.toLocaleString(), color: PURPLE, onClick: () => openDrill({ language: "Spanish" })       },
                            { label: "Spanish %",     value: `${kpis.spaPct}%`,             color: PURPLE, onClick: () => openDrill({ language: "Spanish" })       },
                        ].map(k => (
                            <Box key={k.label} onClick={k.onClick} sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", flex: 1, minWidth: 0,
                                cursor: k.onClick ? "pointer" : "default",
                                transition: "background .15s",
                                "&:hover": k.onClick ? { background: "#efefef" } : {},
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{k.label}</Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                                    {k.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Chart */}
                    <LanguageDailyStackedAreaChart rows={languageData} title="Language" />

                    {/* Table */}
                    <SectionTitle>Detail</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            alignItems: "center",
                        }}>
                            {TABLE_COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    sx={{
                                        textAlign: col.align,
                                        cursor: col.key === "_drill" ? "default" : "pointer",
                                        userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": col.key === "_drill" ? {} : { color: BLUE },
                                    }}
                                >
                                    {col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Rows */}
                        {pagedData.map((row, i) => {
                            const engCount = Number(row.English) || 0;
                            const spaCount = Number(row.Spanish) || 0;
                            return (
                                <Box key={row.ConversationDate ?? i} sx={{
                                    ...gridSx, py: "7px", fontSize: 12,
                                    borderBottom: "0.5px solid", borderColor: "divider",
                                    background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                    "&:hover": { background: "rgba(55,138,221,0.06)" },
                                    alignItems: "center",
                                }}>
                                    {/* drill icon — all languages on this date */}
                                    <Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => openDrill({ date: row.ConversationDate })}
                                            sx={{ width: 24, height: 24 }}
                                            title="View conversations for this date"
                                        >
                                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>

                                    {/* date */}
                                    <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary", whiteSpace: "nowrap" }}>
                                        {formatDate(row.ConversationDate)}
                                    </Box>

                                    {/* English count — clickable */}
                                    <Box
                                        onClick={engCount > 0
                                            ? () => openDrill({ language: "English", date: row.ConversationDate })
                                            : undefined}
                                        sx={{
                                            display: "flex", justifyContent: "flex-end", alignItems: "center",
                                            color: engCount > 0 ? BLUE : "text.disabled",
                                            fontWeight: engCount > 0 ? 500 : 400,
                                            cursor: engCount > 0 ? "pointer" : "default",
                                            "&:hover": engCount > 0 ? { textDecoration: "underline" } : {},
                                        }}
                                    >
                                        {row.English ?? "—"}
                                    </Box>

                                    {/* English % pill — also clickable */}
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                        <PctPill
                                            value={row.EnglishPercentage}
                                            color={BLUE}
                                            clickable={engCount > 0}
                                            onClick={() => openDrill({ language: "English", date: row.ConversationDate })}
                                        />
                                    </Box>

                                    {/* Spanish count — clickable */}
                                    <Box
                                        onClick={spaCount > 0
                                            ? () => openDrill({ language: "Spanish", date: row.ConversationDate })
                                            : undefined}
                                        sx={{
                                            display: "flex", justifyContent: "flex-end", alignItems: "center",
                                            color: spaCount > 0 ? PURPLE : "text.disabled",
                                            fontWeight: spaCount > 0 ? 500 : 400,
                                            cursor: spaCount > 0 ? "pointer" : "default",
                                            "&:hover": spaCount > 0 ? { textDecoration: "underline" } : {},
                                        }}
                                    >
                                        {row.Spanish ?? "—"}
                                    </Box>

                                    {/* Spanish % pill — also clickable */}
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                        <PctPill
                                            value={row.SpanishPercentage}
                                            color={PURPLE}
                                            clickable={spaCount > 0}
                                            onClick={() => openDrill({ language: "Spanish", date: row.ConversationDate })}
                                        />
                                    </Box>

                                    {/* Total */}
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", fontWeight: 600, color: BLUE }}>
                                        {row.Total ?? "—"}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, px: 1 }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, languageData.length)} of {languageData.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Drill-down popup */}
            {drill && (
                <ConversationDrillDownDialog
                    open={!!drill}
                    onClose={() => setDrill(null)}
                    {...drill}
                />
            )}
        </Box>
    );
};

export default Language;